import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { fetcher } from '@weezevent/weezjs-core';
import { useHistory } from 'react-router-dom';
import useAppContext from '../../../../../context/useAppContext';
import { useManagerContext, handleSearchQuery } from '../../../../manager/components/context';

import { Icon } from '@weezevent/nacre';
import { Loader } from 'semantic-ui-react';

import css from './switch-item.module.css';

export default function SwitchItem({ prefix, itemId, primaryKey, type, spec, queryParams }) {
    const history = useHistory();
    const [{ organization, event }] = useAppContext();
    const [contextManager, setManagerContext] = useManagerContext();

    const [itemsIds, setItemsIds] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    const itemIndex = React.useMemo(() => {
        return itemsIds.indexOf(itemId);
    }, [itemsIds, itemId]);

    const itemDisplayPosition = React.useMemo(() => {
        if (!itemsIds.length) {
            return 0;
        }
        return itemIndex + contextManager[`${type}ListPagination`].offset + 1;
    }, [itemsIds, itemIndex, contextManager, type]);

    const handleGetItems = React.useCallback(
        (params = null) => {
            setLoading(true);
            const filtersQuery = contextManager[`${type}ListPagination`];
            let query = {
                ...queryParams,
                ordering: filtersQuery.ordering,
                limit: filtersQuery.pageSize,
                offset: filtersQuery.offset,
                ...handleSearchQuery(spec, contextManager.filters, true)
            };
            if (params) {
                Object.entries(params).forEach(([key, value]) => {
                    query[key] = value;
                });
            }

            return fetcher.get(`/ticket/organizations/${organization.pk}/events/${event.pk}/${type}`, {
                query
            });
        },
        [handleSearchQuery, spec, contextManager, contextManager.filters, event, organization, type, queryParams]
    );

    const onSwitchItem = React.useCallback(
        reverse => {
            const queryParams = contextManager[`${type}ListPagination`];
            const pageToGo = reverse ? Number(queryParams.currentPage - 1) : Number(queryParams.currentPage + 1);
            const indexToGo = reverse ? Number(itemIndex - 1) : Number(itemIndex + 1);
            const indexSwitchPageToGo = reverse ? Number(queryParams.pageSize - 1) : 0;

            const switchItemCondition = reverse ? Boolean(itemDisplayPosition > 1) : Boolean(itemDisplayPosition < contextManager[`${type}ListCount`]);
            const switchPageCondition = reverse ? Boolean(itemIndex === 0) : Boolean(itemIndex + 1 === itemsIds.length);

            if (switchItemCondition) {
                if (switchPageCondition) {
                    const queryParams = contextManager[`${type}ListPagination`];
                    queryParams.offset = pageToGo * queryParams.pageSize;
                    queryParams.currentPage = pageToGo;
                    handleGetItems({
                        offset: queryParams.offset
                    }).then(data => {
                        let _itemsIds = data.response.map(it => it[primaryKey]);
                        setItemsIds(_itemsIds);
                        setManagerContext(ctx => {
                            return { ...ctx, [`${type}ListPagination`]: queryParams };
                        });
                        setLoading(false);
                        return history.push(`${prefix}/${type}/${_itemsIds[indexSwitchPageToGo]}`);
                    });
                } else {
                    return history.push(`${prefix}/${type}/${itemsIds[indexToGo]}`);
                }
            }
        },
        [itemDisplayPosition, history, itemIndex, prefix, itemsIds, contextManager, setManagerContext, handleGetItems, type, primaryKey]
    );

    const canDisplaySwitch = React.useMemo(() => {
        const managerContextKeys = [`${type}ListPagination`, `${type}ListCount`];
        if (!(contextManager[`${type}ListCount`] > 1 && managerContextKeys.every(key => Object.keys(contextManager).includes(key)))) {
            return false;
        }
        // Handle case when an item doesn't match active filters anymore, like after deletion, to avoid displaying inconsistent datas
        return !(itemsIds.length && !itemsIds.includes(itemId));
    }, [itemsIds, itemId, contextManager, type]);

    useEffect(() => {
        if (!contextManager[`${type}ListPagination`] || !contextManager[`${type}ListCount`]) {
            return;
        }
        if (!itemsIds.length && !loading) {
            setLoading(true);
            handleGetItems().then(({ response }) => {
                setItemsIds(response.map(it => it[primaryKey]));
                setLoading(false);
            });
        }
    }, [contextManager, itemId, handleGetItems, loading, itemsIds, type, primaryKey]);

    if (!canDisplaySwitch) {
        return null;
    }

    return (
        <div className={css['switch-item-block']}>
            <SwitchArrow name="left" position={itemDisplayPosition} count={contextManager[`${type}ListCount`]} onClick={onSwitchItem} disabled={loading} reverse />
            <CountTotalLoader loading={loading} count={itemDisplayPosition} total={contextManager[`${type}ListCount`]} />
            <SwitchArrow name="right" position={itemDisplayPosition} count={contextManager[`${type}ListCount`]} onClick={onSwitchItem} disabled={loading} />
        </div>
    );
}

SwitchItem.propTypes = {
    prefix: PropTypes.string.isRequired,
    itemId: PropTypes.number.isRequired,
    primaryKey: PropTypes.string,
    type: PropTypes.string,
    spec: PropTypes.object.isRequired,
    queryParams: PropTypes.object
};

SwitchItem.defaultProps = {
    itemId: null,
    primaryKey: 'id',
    type: 'attendees',
    spec: {},
    queryParams: {}
};

function SwitchArrow({ name, reverse, position, disabled, count, onClick }) {
    const _disabled = React.useMemo(() => {
        if (disabled) {
            return true;
        }
        return name === 'left' ? position === 1 : position === count;
    }, [name, position, count, disabled]);

    return (
        <span className={css['switch-item-arrow']}>
            <Icon name={`chevron-${name}`} onClick={() => onClick(reverse)} disabled={_disabled} />
        </span>
    );
}

SwitchArrow.propTypes = {
    name: PropTypes.string.isRequired,
    reverse: PropTypes.bool,
    position: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired,
    disabled: PropTypes.bool,
    onclick: PropTypes.func.isRequired
};

SwitchArrow.defaultProps = {
    name: 'left',
    reverse: false,
    position: 0,
    count: 0,
    disabled: true,
    onclick: () => {}
};

function CountTotalLoader({ loading, count, total }) {
    return (
        <span className={css['loader-count-container']}>
            {loading ? <Loader className={css['loader-count-loader']} active /> : <span className={css['loader-count-value']}>{count}</span>}
            <span className={css['loader-count-label']}>{` / ${total}`}</span>
        </span>
    );
}

CountTotalLoader.propTypes = {
    loading: PropTypes.bool.isRequired,
    count: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired
};

CountTotalLoader.defaultProps = {
    loading: true,
    count: 0,
    total: 0
};
